import React from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiFunnel } from 'react-icons/pi';
import { ReportFilters } from './types';
import { reportTypeToLabel } from './reportsTypes';
import { FilterTrigger } from '../../components/FilterTrigger';
import { ReportTemplate } from '@ais-ui/websockets';
import { useProfile } from '@ais-ui/profile';

interface ReportFilterProps {
  reportFilters: ReportFilters;
  setReportFilters: React.Dispatch<React.SetStateAction<ReportFilters>>;
  reportTemplates: ReportTemplate[] | undefined;
}

// Create report type options from your existing mapping
const getReportTypeOptions = () => {
  return Object.entries(reportTypeToLabel)
    .filter(([type]) => type !== 'REPLAY_VIDEO')
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Extract unique owners from report templates
const getOwnerOptions = (
  reportTemplates: ReportTemplate[] | undefined,
  currentUsername: string | undefined
) => {
  if (!reportTemplates || reportTemplates.length === 0) {
    return [];
  }

  const uniqueOwners = [
    ...new Set(
      reportTemplates.map((template) => template.tplOwner).filter(Boolean)
    ),
  ];

  // Separate current user from other owners
  const isCurrentUser = (owner: string) => owner === currentUsername;
  const currentUserOwner = uniqueOwners.find(isCurrentUser);
  const otherOwners = uniqueOwners
    .filter((owner) => !isCurrentUser(owner))
    .sort((a, b) => a.localeCompare(b));

  // Build the final list with current user first (as "Me")
  const result: Array<{ value: string; label: string }> = [];

  if (currentUserOwner) {
    result.push({ value: currentUserOwner, label: 'Me' });
  }

  otherOwners.forEach((owner) => {
    result.push({ value: owner, label: owner });
  });

  return result;
};

export const ReportFilter = ({
  reportFilters,
  setReportFilters,
  reportTemplates,
}: ReportFilterProps) => {
  const reportTypeOptions = getReportTypeOptions();
  const { username } = useProfile();
  const ownerOptions = getOwnerOptions(reportTemplates, username);

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (reportFilters.status !== 'all') count++;
    if (reportFilters.scheduled !== 'all') count++;
    if (reportFilters.reportType !== 'all') count++;
    if (reportFilters.owner && reportFilters.owner !== 'all') count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <FilterTrigger
              activeFilterCount={activeFiltersCount}
            ></FilterTrigger>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent>
          {activeFiltersCount > 0
            ? `Filter reports (${activeFiltersCount} active)`
            : 'Filter reports'}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-64 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm">Filter Reports</div>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFiltersCount} active
            </Badge>
          )}
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Status</div>
          <Select
            value={reportFilters.status}
            onValueChange={(value) =>
              setReportFilters((prev) => ({
                ...prev,
                status: value as ReportFilters['status'],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="executing">Executing</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="finished">Finished</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Scheduled Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Schedule</div>
          <Select
            value={reportFilters.scheduled}
            onValueChange={(value) =>
              setReportFilters((prev) => ({
                ...prev,
                scheduled: value as ReportFilters['scheduled'],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reports</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="unscheduled">Unscheduled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Report Type Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Report Type</div>
          <Select
            value={reportFilters.reportType}
            onValueChange={(value) =>
              setReportFilters((prev) => ({
                ...prev,
                reportType: value as ReportFilters['reportType'],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {reportTypeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Owner Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Owner</div>
          <Select
            value={reportFilters.owner}
            onValueChange={(value) =>
              setReportFilters((prev) => ({
                ...prev,
                owner: value as ReportFilters['owner'],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All Owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Owners</SelectItem>
              {ownerOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters Button */}
        <div className="pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() =>
              setReportFilters({
                status: 'all',
                type: 'all',
                scheduled: 'all',
                reportType: 'all',
                owner: 'all',
              })
            }
            disabled={activeFiltersCount === 0}
          >
            Clear Filters
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
